import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  Dot,
  Image,
  ImageWithZoom,
  Slide,
  Slider,
} from 'pure-react-carousel';
import { SVG } from '@glowforge/ui-common/components';

import CarouselDescription from './carousel-description';
import CarouselIndicator from './carousel-indicator';
import CarouselChangeHandler from './carousel-change-handler';
import './image-carousel.less';

/**
 * Show Images in a carousel with zoom! Reference
 * https://github.com/express-labs/pure-react-carousel
 * @param {Array} Images see proptypes. Full size images.
 * @param {Array} Images see proptypes. Should be thumbnail size.
 * @param {Number} naturalSlideHeight Used to calculate image ratio along with
 * naturalSlideWidth
 * @param {Number} naturalSlideWidth Used to calculate image ratio along with
 * naturalSlideHeight
 * @param {String} className Class applied to outer carousel component
 * @param {(currentSlide: number) => unknown} onChange Called when current slide
 * index is updated
 */
const ImageCarousel = ({
  images,
  thumbnails,
  naturalSlideHeight,
  naturalSlideWidth,
  className,
  showNav,
  showIndicator,
  onChange,
}) => {
  // Force provider to remount when images change, which will reset the gallery
  // state
  const [providerKey, setProviderKey] = useState(0);
  useEffect(() => setProviderKey(providerKey + 1), [images]);

  const imageSlides = _.map(images, ({ src, alt }, index) => (
    <Slide index={index} key={src}>
      <ImageWithZoom src={src} alt={alt} />
    </Slide>
  ));

  // Gather descriptions
  const descriptions = _.reduce(images, (result, { description }) => {
    result.push(description);
    return result;
  }, []);

  // These are only displayed if there are multiple images
  let carouselThumbs = null;
  let carouselNav = null;
  let indicator = null;


  // Options for carousel
  // touch and drag not necessary for single images, there's nothing to drag to!
  let touchEnabled = false;
  let dragEnabled = false;

  const hasMultipleImages = images.length > 1;

  if (hasMultipleImages) {
    // Thumbnails
    const thumbSlides = _.map(thumbnails, ({ src, alt }, index) => (
      <Dot
        slide={index}
        className='clearButton'
        key={src}>
        <Image src={src} alt={alt} />
      </Dot>
    ));
    carouselThumbs = <div className='carouselThumbs'>{thumbSlides}</div>;

    // Forward / Back buttons
    carouselNav = showNav ? (
      <>
        <ButtonBack className='carouselNav carouselNav-back clearButton'>
          <SVG id='chevron-medium' />
        </ButtonBack>
        <ButtonNext className='carouselNav carouselNav-next clearButton'>
          <SVG id='chevron-medium' />
        </ButtonNext>
      </>
    ) : null;

    // Horizontal bar below carousel.
    indicator = showIndicator ? <CarouselIndicator /> : null;

    // enable touch and drag events
    touchEnabled = true;
    dragEnabled = true;
  }

  return (
    <CarouselProvider
      key={providerKey}
      naturalSlideWidth={naturalSlideWidth}
      naturalSlideHeight={naturalSlideHeight}
      totalSlides={images.length}
      className={className}
      touchEnabled={touchEnabled}
      dragEnabled={dragEnabled}>
      <div className='carouselSlides'>
        <Slider>
          {imageSlides}
        </Slider>
        {carouselNav}
      </div>
      <CarouselDescription className='imageDescription' descriptions={descriptions} />
      {indicator}
      {carouselThumbs}
      <CarouselChangeHandler onChange={onChange} />
    </CarouselProvider>
  );
};

ImageCarousel.propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
    description: PropTypes.string,
  })),
  thumbnails: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  })),
  naturalSlideHeight: PropTypes.number,
  naturalSlideWidth: PropTypes.number,
  className: PropTypes.string,
  showNav: PropTypes.bool,
  showIndicator: PropTypes.bool,
  onChange: PropTypes.func,
};

ImageCarousel.defaultProps = {
  images: [],
  thumbnails: [],
  naturalSlideHeight: null,
  naturalSlideWidth: null,
  className: null,
  showNav: true,
  showIndicator: true,
  onChange: () => {},
};

export default ImageCarousel;
